import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ActivityContext = createContext(null);

export const useActivity = () => {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error('useActivity must be used within ActivityProvider');
  return ctx;
};

const STORAGE_KEY = 'ac_activities_v1';

export const ActivityProvider = ({ children }) => {
  const [activities, setActivities] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(activities)); } catch {}
  }, [activities]);

  const genId = () => {
    try {
      if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch {}
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  const log = (event) => {
    const withMeta = { id: genId(), ts: new Date().toISOString(), ...event };
    setActivities((prev) => [withMeta, ...prev].slice(0, 2000)); // cap for safety
    return withMeta;
  };

  const stats = useMemo(() => {
    const summary = { total: activities.length, uploads: 0, verifications: 0, validations: 0, anomalies: 0 };
    const perInstitution = {};
    for (const a of activities) {
      if (a.action === 'upload') summary.uploads++;
      if (a.action === 'verify') summary.verifications++;
      if (a.action === 'validate') summary.validations++;
      if (a.flags && a.flags.length) summary.anomalies += a.flags.length;
      const inst = a.institution || 'Unknown';
      if (!perInstitution[inst]) perInstitution[inst] = { uploads: 0, verifications: 0, validations: 0, anomalies: 0 };
      if (a.action === 'upload') perInstitution[inst].uploads++;
      if (a.action === 'verify') perInstitution[inst].verifications++;
      if (a.action === 'validate') perInstitution[inst].validations++;
      if (a.flags && a.flags.length) perInstitution[inst].anomalies += a.flags.length;
    }
    return { summary, perInstitution };
  }, [activities]);

  const value = { activities, log, stats };
  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
};

export default ActivityContext;
